import React, { createContext, useContext, useState, useMemo } from 'react';

const DateRangeContext = createContext(null);

function toYMD(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function computeRange(period, offset, customFrom, customTo) {
  const now = new Date();

  if (period === 'today') {
    const base = new Date(now);
    base.setDate(base.getDate() + offset);
    const dateStr = toYMD(base);
    const label = offset === 0 ? 'Today' : base.toLocaleString('en-KE', { day: 'numeric', month: 'long', year: 'numeric' });
    return { from: dateStr, to: dateStr, label };
  }

  if (period === 'week') {
    const base = new Date(now);
    const dow = base.getDay() === 0 ? 6 : base.getDay() - 1; // Mon=0
    base.setDate(base.getDate() - dow + offset * 7);
    const end = new Date(base);
    end.setDate(base.getDate() + 6);
    const from = toYMD(base);
    const to   = toYMD(end);
    const fmt  = (d) => d.toLocaleString('en-KE', { day: 'numeric', month: 'short' });
    const label = `${fmt(base)} – ${fmt(end)} ${end.getFullYear()}`;
    return { from, to, label };
  }

  if (period === 'year') {
    const year = now.getFullYear() + offset;
    return { from: `${year}-01-01`, to: `${year}-12-31`, label: String(year) };
  }

  if (period === 'custom') {
    const label = customFrom && customTo ? `${customFrom} – ${customTo}` : 'Pick dates';
    return { from: customFrom || '', to: customTo || '', label };
  }

  // default: month
  const base = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const from  = toYMD(base);
  const to    = toYMD(new Date(base.getFullYear(), base.getMonth() + 1, 0));
  const label = base.toLocaleString('en-KE', { month: 'long', year: 'numeric' });
  return { from, to, label };
}

export function DateRangeProvider({ children }) {
  const [period, setPeriod]         = useState('today');
  const [offset, setOffset]         = useState(0);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo]     = useState('');

  const { from, to, label } = useMemo(
    () => computeRange(period, offset, customFrom, customTo),
    [period, offset, customFrom, customTo]
  );

  const goBack    = () => setOffset(o => o - 1);
  const goForward = () => setOffset(o => Math.min(o + 1, 0));
  const goNow     = () => { setOffset(0); };

  const changePeriod = (p) => { setPeriod(p); setOffset(0); };

  const nowLabel = period === 'today' ? 'Today'
    : period === 'week'  ? 'This Week'
    : period === 'year'  ? 'This Year'
    : 'This Month';

  return (
    <DateRangeContext.Provider value={{
      period, changePeriod,
      offset, goBack, goForward, goNow,
      customFrom, setCustomFrom,
      customTo,   setCustomTo,
      from, to, label, nowLabel,
    }}>
      {children}
    </DateRangeContext.Provider>
  );
}

export const useDateRange = () => useContext(DateRangeContext);
